import { HubSelect } from "@/components/HubSelect";
import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@bednbody/ui/components/empty";
import { Input } from "@bednbody/ui/components/input";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import {
  ArrowsLeftRightIcon,
  FlagIcon,
  HourglassMediumIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  PlusIcon,
  UserCircleCheckIcon,
  UserCircleDashedIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { isServiceDate } from "../iso-week";
import {
  type RiderSessionRow,
  useCreateEmergencyRiderSession,
  useReassignRiderSession,
  useReleaseRiderSession,
  useRetimeRiderSession,
  useRiderSessionsForDate,
  useVoidRiderSession,
} from "../rider-sessions.hooks";
import { EmergencySessionDialog } from "./EmergencySessionDialog";
import { SessionOverrideDialog } from "./SessionOverrideDialog";
import { StateSummary } from "./StateSummary";

/** The eight lifecycle states (0063), in the order a Session moves through them. */
const STATE_META: Record<string, { label: string; variant: BadgeVariant }> = {
  secured: { label: "Secured", variant: "muted" },
  pre_start_waiting: { label: "Waiting", variant: "info" },
  active: { label: "Active", variant: "success" },
  paused: { label: "Paused", variant: "warning" },
  emergency_paused: { label: "Emergency pause", variant: "destructive" },
  extended: { label: "Extended", variant: "primary" },
  session_done: { label: "Done", variant: "outline" },
  unable_to_continue: { label: "Unable to continue", variant: "destructive" },
};

const timeOf = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString("en-PH", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "Asia/Manila",
      })
    : "—";

interface LiveSessionsPanelProps {
  /** The hub manager's own hub; null for roles that see every hub. */
  hubId: string | null;
  /** Hubs an office role may narrow to. Unused when `hubId` is set. */
  hubs: { id: string; name: string }[];
  /** Admin/superadmin — the only roles the Session override routes accept. */
  canManage: boolean;
  /** Superadmin only (0245) — narrower than canManage; retime 403s Admin. */
  canRetime: boolean;
  serviceDate: string;
  onServiceDateChange: (date: string) => void;
}

/**
 * Live monitoring — every Session on one service date, by state.
 *
 * Refreshes every 15 s. The states that matter most are written by the pg_cron
 * minute tick rather than by anyone clicking anything, so a stale view here
 * looks exactly like a working one — hence the visible refresh note.
 */
export function LiveSessionsPanel({
  hubId,
  hubs,
  canManage,
  canRetime,
  serviceDate,
  onServiceDateChange,
}: LiveSessionsPanelProps) {
  // An office role starts on every hub and may narrow to one. A Manual Session
  // needs exactly one, and before this picker existed an office role had no way
  // to choose it — the button was disabled for them for good.
  const [pickedHubId, setPickedHubId] = useState("");
  const effectiveHubId = hubId ?? (pickedHubId || null);
  const effectiveHubName = hubs.find((hub) => hub.id === effectiveHubId)?.name ?? null;
  const hasDate = isServiceDate(serviceDate);

  const { data, isLoading, isError } = useRiderSessionsForDate(serviceDate, effectiveHubId);
  const sessions = hasDate ? (data ?? []) : [];

  const [overriding, setOverriding] = useState<RiderSessionRow | null>(null);
  const [creating, setCreating] = useState(false);
  const reassign = useReassignRiderSession();
  const release = useReleaseRiderSession();
  const voidSession = useVoidRiderSession();
  const retime = useRetimeRiderSession();
  const createEmergency = useCreateEmergencyRiderSession();

  const countOf = (...states: string[]) =>
    sessions.filter((s) => states.includes(s.state ?? "")).length;

  /**
   * OQ-122 — a Session may only be moved before it starts. 0193 refuses anything
   * else (`SESSION_ALREADY_STARTED`) because one `rider_session_earnings` row
   * cannot have two earners, so the button is hidden rather than shown disabled:
   * a control that can never succeed is noise on a monitoring screen.
   */
  const canOverride = (session: RiderSessionRow) =>
    canManage &&
    session.state === "secured" &&
    !session.started_at &&
    !session.voided_at &&
    new Date(session.ends_at) > new Date();

  const pending =
    reassign.isPending || release.isPending || voidSession.isPending || retime.isPending;
  const overrideError = reassign.error ?? release.error ?? voidSession.error ?? retime.error;

  const closeOverride = () => {
    setOverriding(null);
    reassign.reset();
    release.reset();
    voidSession.reset();
    retime.reset();
  };

  const closeCreate = () => {
    setCreating(false);
    createEmergency.reset();
  };

  return (
    <div className="space-y-8">
      <StateSummary
        isLoading={hasDate && isLoading}
        items={[
          {
            label: "Unclaimed",
            value: sessions.filter((s) => !s.secured_rider_id).length,
            icon: UserCircleDashedIcon,
          },
          // An unclaimed Session is ALSO state `secured` (it is published supply),
          // so counting the state alone put every unclaimed row in both tiles.
          {
            label: "Secured",
            value: sessions.filter((s) => s.state === "secured" && s.secured_rider_id).length,
            icon: UserCircleCheckIcon,
            tone: "text-muted-foreground",
          },
          {
            label: "Waiting",
            value: countOf("pre_start_waiting"),
            icon: HourglassMediumIcon,
            tone: "text-info",
          },
          {
            label: "Active",
            value: countOf("active", "extended"),
            icon: PlayCircleIcon,
            tone: "text-success",
          },
          {
            label: "Paused",
            value: countOf("paused", "emergency_paused"),
            icon: PauseCircleIcon,
            tone: "text-warning",
          },
          {
            label: "Finished",
            value: countOf("session_done", "unable_to_continue"),
            icon: FlagIcon,
            tone: "text-foreground",
          },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Sessions</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {hubId === null ? (
              <>
                <label htmlFor="live-hub" className="sr-only">
                  Hub
                </label>
                <HubSelect
                  ariaLabel="Hub"
                  className="w-48"
                  hubs={hubs}
                  id="live-hub"
                  variant="combobox"
                  value={pickedHubId}
                  onValueChange={(hubId) => setPickedHubId(hubId ?? "")}
                />
              </>
            ) : null}
            <label htmlFor="live-date" className="sr-only">
              Service date
            </label>
            <Input
              id="live-date"
              type="date"
              value={serviceDate}
              onChange={(e) => onServiceDateChange(e.target.value)}
              className="w-auto"
            />
            <span className="text-xs text-muted-foreground">refreshes every 15s</span>
            {/* Manual creation is the one operation CORE-05B forbids by name, so
                it sits apart from the monitoring controls and is disabled until
                a single Dispatch Area is chosen — a Session belongs to exactly
                one, and "all hubs" is not an answer the server can take. */}
            {canManage ? (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                disabled={effectiveHubId === null}
                title={effectiveHubId === null ? "Choose a hub first" : undefined}
                onClick={() => setCreating(true)}
              >
                <PlusIcon />
                Manual Session
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {!hasDate ? (
            <p className="text-sm text-muted-foreground">Choose a service date.</p>
          ) : isError ? (
            <p className="text-sm text-destructive">
              Couldn&rsquo;t load Sessions — check your connection and try again.
            </p>
          ) : isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nothing generated for this date</EmptyTitle>
                <EmptyDescription>
                  Sessions appear here once a batch has been generated for it.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ul className="divide-y divide-border/60">
              {sessions.map((session: RiderSessionRow) => {
                const meta = session.state ? STATE_META[session.state] : undefined;
                return (
                  <li key={session.id} className="flex items-center gap-3 py-2.5">
                    <Badge variant="outline" className="shrink-0 font-mono">
                      {session.code}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {session.staff?.full_name ?? (
                          <span className="font-normal text-muted-foreground">Unclaimed</span>
                        )}
                        {session.staff?.rider_rank ? (
                          <span className="ml-2 text-xs font-normal capitalize text-muted-foreground">
                            {session.staff.rider_rank}
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {timeOf(session.starts_at)} – {timeOf(session.ends_at)}
                        {session.started_at ? ` · started ${timeOf(session.started_at)}` : ""}
                        {session.actual_extension_minutes
                          ? ` · +${session.actual_extension_minutes}m`
                          : ""}
                        {session.hubs?.name ? ` · ${session.hubs.name}` : ""}
                      </p>
                    </div>
                    {/* §4.5.5 — a record still published to Diamond only has not
                        reached general access yet, which is why it can look
                        unclaimed while riders exist who cannot see it. */}
                    {session.access === "diamond" && !session.secured_rider_id ? (
                      <Badge variant="gold" className="shrink-0">
                        Diamond only
                      </Badge>
                    ) : null}
                    {/* 0196's whole point, surfaced. A manually created Session
                        that looked identical to a generated one is exactly what
                        the quarantine exists to prevent — so the row says which
                        it is, rather than the operator having to know. */}
                    {session.rider_session_batches?.origin === "emergency_override" ? (
                      <Badge variant="warning" className="shrink-0">
                        Manual
                      </Badge>
                    ) : null}
                    {session.voided_at ? (
                      <Badge variant="outline" className="shrink-0">
                        Voided
                      </Badge>
                    ) : meta ? (
                      <Badge variant={meta.variant} className="shrink-0">
                        {meta.label}
                      </Badge>
                    ) : null}
                    {canOverride(session) ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={() => setOverriding(session)}
                      >
                        <ArrowsLeftRightIcon />
                        {session.secured_rider_id ? "Reassign" : "Assign"}
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <SessionOverrideDialog
        session={overriding}
        canRetime={canRetime}
        isPending={pending}
        errorMessage={overrideError ? overrideError.message : null}
        onClose={closeOverride}
        onReassign={(input) =>
          reassign.mutate({ sessionId: overriding!.id, ...input }, { onSuccess: closeOverride })
        }
        onRelease={(input) =>
          release.mutate({ sessionId: overriding!.id, ...input }, { onSuccess: closeOverride })
        }
        onVoid={(input) =>
          voidSession.mutate({ sessionId: overriding!.id, ...input }, { onSuccess: closeOverride })
        }
        onRetime={(input) =>
          retime.mutate({ sessionId: overriding!.id, ...input }, { onSuccess: closeOverride })
        }
      />

      <EmergencySessionDialog
        open={creating}
        hubId={effectiveHubId}
        hubName={effectiveHubName ?? sessions.find((s) => s.hubs?.name)?.hubs?.name ?? null}
        serviceDate={serviceDate}
        isPending={createEmergency.isPending}
        errorMessage={createEmergency.error ? createEmergency.error.message : null}
        onClose={closeCreate}
        onSubmit={(input) => {
          if (!effectiveHubId) return;
          createEmergency.mutate({ hubId: effectiveHubId, ...input }, { onSuccess: closeCreate });
        }}
      />
    </div>
  );
}
